<template>
  <div class="flex flex-col h-full">
    <div class="flex justify-between items-center mb-2 md:mb-4">
      <div :class="titleStyle" class="font-bold text-base lg:text-xl">
        {{ title }}
      </div>
      <div>
        <div class="w-full space-x-2 flex">
          <div
            class="topa-btn px-2 text-xs font-bold w-14 h-8"
            :class="{
              'topa-btn__active': state.selected == i,
            }"
            v-for="(text, i) in ['1Y', 'ALL']"
            :key="i"
            @click="state.selected = i"
          >
            {{ text }}
          </div>
        </div>
      </div>
    </div>
    <div class="flex-1 min-h-65 h-full py-2 md:py-0" ref="container">
      <VChart
        ref="chart"
        v-if="!loading"
        class="w-full min-h-65 md:min-h-85.75 h-full"
        :style="`height:${chartHeight}`"
        :option="option"
        :key="width"
      ></VChart>
      <div
        v-else-if="loading"
        class="w-full h-full flex justify-center items-center"
      >
        <Spin></Spin>
      </div>
      <div v-else class="flex flex-col items-center justify-center">
        <img
          src="@/assets/bgs/no-data-chart.png"
          alt="no daos data found"
          class="relative"
          style="width: 236px; height: 160px"
        />
        <div class="text-white-040">No Data</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { queryProposalChart, queryProposalChartEco } from '@/api/dao';
import Spin from '@/components/Spin.vue';
import { chainsSelect, chartColor, typeSelection } from '@/constants';
import { useReqByBool } from '@/hooks';
import { formatVal } from '@/utils';
import { useElementSize } from '@vueuse/core';
import { LineChart } from 'echarts/charts';
import {
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { max } from 'lodash';
import { ref, computed, inject, onMounted, reactive, watch } from 'vue';
import VChart from 'vue-echarts';
import { homeChains, homeTypes } from '../../constant';
const props = defineProps({
  ecoCode: String,
  type: { type: String, default: null },
  chain: { type: String || Number, default: null },
  title: { type: String, default: 'Proposals And Votes Added Per Month' },
  titleStyle: { type: String, default: '' },
});
const chart = ref(null);

const container = ref(null);
const { width, height } = useElementSize(container);
const chartHeight = computed(() => Math.max(height - 60, 260 - 60)); // Ensuring a minimum height of 260px

const isSmallScreen = computed(() => width.value < 786);
use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  GraphicComponent,
]);

const homeState = inject('homeState');

const state = reactive({
  selected: 0,
  checked: true,
  list: [],
});

const data = computed(() => {
  const list = JSON.parse(JSON.stringify(state.list));
  if (state.selected == 0) {
    let x = list.reverse();
    x = x.slice(0, 12);
    x.reverse();
    return x;
  }
  return list;
});

const option = computed(() => {
  const aY = Math.max(
    max(data.value.map((x) => x.voters)),
    max(data.value.map((x) => x.votes)) * 1.05,
  );
  const bY = max(data.value.map((x) => x.proposals)) * 1.05;

  return {
    tooltip: {
      trigger: 'axis',
      borderColor: '#FFFFFF66',
      backgroundColor: '#1e1f21bb',
      textStyle: {
        color: '#fff',
        fontSize: 12,
      },
      axisPointer: {
        type: 'cross',
        label: {
          show: false,
          backgroundColor: '#6a7985',
        },
      },
      formatter: (conf) => {
        const [Voters, Votes, Proposal] = conf;

        return `
          <div class="space-y-1">
            <div>${Voters.axisValue}</div>
            <div class="flex items-center">
              <div class="w-3 mr-8" style="height: 2px; background: #4895efff"></div>
              Proposal: ${formatVal(Proposal.value, { omit: true })}
            </div>
            
            <div class="flex items-center">
              <div class="w-3 h-3 rounded mr-8" style="background: #f72585ff"></div>
              Voters: ${formatVal(Voters.value, { omit: true })}</div>
          </div>
          <div class="flex items-center">
              <div class="w-3 h-3 rounded mr-8" style="background: #9317b5ff"></div>
              Votes: ${formatVal(Votes.value, { omit: true })}
            </div>
        `;
      },
    },
    grid: {
      top: isSmallScreen.value ? 40 : 60,
      left: isSmallScreen.value ? 15 : 40,
      right: isSmallScreen.value ? 15 : 30,
      bottom: isSmallScreen.value ? 30 : 20,
      height: chartHeight.value,
      containLabel: true,
    },
    legend: {
      data: ['Proposal', 'Voters', 'Votes'],
      textStyle: {
        color: '#fff',
      },
      left: isSmallScreen.value ? 'center' : 'left',
      top: isSmallScreen.value ? 'bottom' : 'top',
    },
    xAxis: {
      type: 'category',
      data: data.value.map((x) => x.month),
    },
    yAxis: [
      {
        type: 'value',
        splitLine: {
          show: false,
          lineStyle: {
            color: '#FFFFFF1A',
          },
        },
        axisLabel: {
          formatter: (val) => formatVal(val, { omit: true }),
        },
        splitNumber: 5,
        max: bY,
        interval: bY / 5,
        name: 'Proposal:',
      },
      {
        type: 'value',
        splitLine: {
          lineStyle: {
            color: '#FFFFFF1A',
          },
        },
        axisLabel: {
          formatter: (val) => formatVal(val, { omit: true }),
        },
        splitNumber: 5,
        max: aY,
        interval: aY / 5,
        name: 'Votes / Voters:',
      },
    ],
    color: chartColor,
    series: [
      {
        data: data.value.map((x) => x.voters),
        type: 'bar',
        name: 'Voters',
        yAxisIndex: 1,
      },
      {
        data: data.value.map((x) => x.votes),
        type: 'bar',
        name: 'Votes',
        yAxisIndex: 1,
      },
      {
        data: data.value.map((x) => x.proposals),
        type: 'line',
        name: 'Proposal',
        color: '#4895EFFF',
        yAxisIndex: 0,
      },
    ],
  };
});

const { loadData, loading } = useReqByBool(async () => {
  const res = props.ecoCode
    ? await queryProposalChartEco(props.ecoCode)
    : await queryProposalChart(
        props.type !== null
          ? typeSelection[props.type].value
          : homeTypes[homeState?.selectedType].value,
        props.chain !== null
          ? chainsSelect[props.chain].value
          : homeChains[homeState?.selectedChain].value,
      );
  state.list = res;
});

watch(
  () => [
    homeState?.selectedType,
    homeState?.selectedChain,
    props.type,
    props.chain,
  ],
  loadData,
);

watch(width, () => {
  if (chart.value) {
    chart.value.setOption(option.value);
  }
});
onMounted(loadData);
</script>

<style scoped>
.topa-btn {
  height: 32px;
  border-radius: 8px;
  border: 1px solid #2a2e33;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
}
.topa-btn__active {
  background: #ff5585ff;
  border: 1px solid #ff5585ff;
}
</style>
